import { useEffect, useState } from "react";
import { Check, Copy, Trash2, Link2 } from "lucide-react";
import { SharedLink, sharesApi } from "@/lib/catalog-api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/catalog/Skeleton";
import { toast } from "sonner";

export default function SharedLinks() {
  const [links, setLinks] = useState<SharedLink[] | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  function load() {
    sharesApi.list().then(setLinks).catch(() => setLinks([]));
  }
  useEffect(() => { load(); }, []);

  async function revoke(link: SharedLink) {
    if (!window.confirm("Revoke this share link? Anyone holding it loses access.")) return;
    try {
      await sharesApi.revoke(link._id);
      toast.success("Link revoked.");
      load();
    } catch (e: any) {
      toast.error(e?.message || "Could not revoke.");
    }
  }

  async function copy(link: SharedLink) {
    const url = `${window.location.origin}/share/${link.token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(link._id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      toast.error("Copy failed.");
    }
  }

  return (
    <main className="flex-1 px-6 md:px-10 py-10" data-testid="shared-links-page">
      <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-2">— Settings</p>
      <h1 className="font-display text-3xl sm:text-4xl">Shared lookbook links</h1>
      <p className="text-sm text-muted-foreground mt-2 max-w-prose">
        Every link you've created. Revoke any time — that immediately blocks anyone
        still holding it.
      </p>

      <div className="mt-10">
        {links === null ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
          </div>
        ) : links.length === 0 ? (
          <div className="border border-border bg-bone/30 p-12 text-center" data-testid="shared-links-empty">
            <Link2 className="w-6 h-6 mx-auto text-oxblood" strokeWidth={1.4} />
            <p className="font-display text-2xl mt-4">No links yet.</p>
            <p className="text-sm text-muted-foreground mt-2">
              Open any outfit and use Share to send a clean public lookbook to a client.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border border-y border-border" data-testid="shared-links-list">
            {links.map((link) => {
              const expired = link.expires_at && new Date(link.expires_at).getTime() < Date.now();
              const status = link.revoked ? "revoked" : expired ? "expired" : "active";
              const cls =
                status === "active" ? "bg-foreground/5 text-foreground" :
                status === "expired" ? "bg-bone text-muted-foreground" :
                "bg-oxblood/15 text-oxblood-deep";
              return (
                <li key={link._id}
                  data-testid={`share-row-${link._id}`}
                  className="grid grid-cols-[1fr_auto] gap-4 px-4 py-4 items-center">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-display text-base truncate">{link.title || "Untitled lookbook"}</p>
                      <span className={`text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 ${cls}`}>{status}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 font-mono truncate">/share/{link.token}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {link.view_count} view{link.view_count === 1 ? "" : "s"}
                      {link.expires_at ? ` · expires ${new Date(link.expires_at).toLocaleDateString()}` : " · no expiry"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!link.revoked && !expired && (
                      <Button variant="outline" size="sm"
                        data-testid={`share-copy-${link._id}`}
                        onClick={() => copy(link)}
                        className="rounded-none border-foreground/20 hover:bg-bone h-9">
                        {copiedId === link._id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    )}
                    {!link.revoked && (
                      <Button variant="outline" size="sm"
                        data-testid={`share-revoke-${link._id}`}
                        onClick={() => revoke(link)}
                        className="rounded-none border-foreground/20 hover:bg-bone text-oxblood h-9">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </main>
  );
}
